import {html, HTMLTemplateResult, nothing, PropertyValues} from "lit";
import {AbstractO2dElement} from "@o2d/shared";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {state} from "lit/decorators/state.js";
import {styleMap} from "lit/directives/style-map.js";
import {classMap} from "lit/directives/class-map.js";
import {repeat} from "lit/directives/repeat.js";
import type {DateTime} from "luxon";
import {container} from "tsyringe";

import {IssuePersonalDataRepository} from "../../domain/repositories/issue-personal-data.repository";
import {DateHelper} from "../../helpers/date.helper";
import {JustMovedFlashController} from "../_reactive-controllers/just-moved-flash.controller";
import {DissolveFlashController} from "../_reactive-controllers/dissolve-flash.controller";
import {RxjsLifecycleController} from "../_reactive-controllers/rxjs-lifecycle.controller";
import {diffDoneRows} from "../../domain/helpers/diff-done-rows.helper";
import {CommandBus} from "../../services/command-bus.service";
import {CreateIssuePersonalDataCommand} from "../../commands/create-issue-personal-data.command";
import {UpdateIssuePersonalDataCommand} from "../../commands/update-issue-personal-data.command";
import {IssueHelper} from "../../domain/helpers/issue.helper";
import type {Issue} from "../../domain/entities/issue.entity";
import type {IssuePersonalData} from "../../domain/entities/issue-personal-data.entity";
import {
    MY_ORGANIZER_ISSUE_DATED_EVENT,
    MyOrganizerDragContext,
    type MyOrganizerIssueDatedEventDetail,
} from "./my-organizer-drag-context";
import {rootUidOf} from "../../domain/helpers/issue-type-root.helper";
import {itemColorStyle} from "../../helpers/header-item-style.helper";

const DATE_FORMAT = {day: `2-digit`, month: `2-digit`, year: `numeric`} as const;

/** A row in this cell, paired with its resolved Issue for rendering — same
 *  shape `<my-organizer-label-column>` uses for its own rows. */
export interface CalendarDayRow {
    issue: Issue;
    row: IssuePersonalData;
}

/**
 * "Mein Kalender" (Anke, 2026-09-07, "in die 14 boxen montag, dienstag etc.
 * soll man dann die tickets schieben können... wenn ich ein ticket in eine
 * solche tagesbox verschiebe, dann wird dieses datum als das individuelle
 * datum abgespeichert"): one day cell of the 7-column grid, one Mon–Sun block
 * per week (three weeks since 2026-10-16) — a drop target that sets a dropped Issue's
 * `personalDate` to THIS cell's own `date`.
 *
 * Deliberately the SIMPLE half of `<my-organizer-label-column>`: no manual
 * within-cell reordering (no gap-drop rectangles, no `sortOrder` bookkeeping)
 * — Anke's request only ever describes ONE thing happening on a drop
 * ("dieses Datum wird abgespeichert"), not a position within the day. Rows
 * are rendered in the order the initial `.rows` property hands them; a
 * freshly-dropped Issue is appended.
 *
 * `.rows` is seeded ONCE from the parent view (`o2d-personal-organizer-
 * calendar.view.ts`), which loads every one of the current User's own
 * `IssuePersonalData` rows a single time and buckets them by ISO date — 21
 * separate `findAllMine()` calls (one per cell) would be wasteful for data
 * that small. After that first seed, this cell manages its OWN `_rows`
 * locally (mirroring how `<my-organizer-label-column>` self-manages after
 * its own load), reacting to the shared drag-context events for cross-cell
 * sync exactly like the label columns do for each other.
 *
 * Live-refresh (Anke, 2026-09-09, follow-up to the Boxen/Inbox live-refresh:
 * "ich möchte, dass das Prinzip weiter genutzt wird") is wired at the PARENT
 * view, not per-cell — 21 separate `EntityLiveRefreshController`s all
 * reacting to the same `entity.changed` event would mean 21 redundant
 * `findAllMine()` calls for the exact same reload the parent already does
 * once. The parent re-runs its own load, then pushes each cell's fresh, RAW
 * bucket in directly via `liveResync()` below (bypassing the `.rows`
 * property/seed-once guard on purpose — see that method's own doc comment).
 * "RAW" matters: filtering out a DONE Issue, and animating one that just
 * WENT done, both happen INSIDE this cell (`willUpdate`/`liveResync`), not
 * in the parent — see those methods' own doc comments.
 *
 * Extends `AbstractO2dElement` (`@o2d/shared`), not the legacy
 * `AbstractLitElement` — mandatory for new components per ADR-007.
 */
@customElement('my-organizer-calendar-day')
export class MyOrganizerCalendarDayComponent extends AbstractO2dElement {

    /**
     * Definite-assignment (`!`) since this cell is always given a `.date` by
     * its only caller (`o2d-personal-organizer-calendar.view.ts`) — but see
     * `render()`/`_onDrop()` for a runtime guard anyway (review-fund,
     * 2026-09-07): a future reuse of this component that forgets to bind
     * `.date` should fail loud/gracefully instead of an unclear `TypeError`
     * deep inside Luxon.
     */
    @property({attribute: false})
    public date!: DateTime;

    /** The German weekday name ("Montag", ...) — the view computes this once
     *  for all 21 cells rather than every cell duplicating a locale lookup. */
    @property({type: String})
    public label = '';

    @property({attribute: false})
    public rows: CalendarDayRow[] = [];

    /**
     * The calendar row's entries on this day (public holidays, absences — Anke, 2026-10-16),
     * handed in by the view on every load. Unlike `rows` this is plain display state: shown
     * small and muted under the date, title as tooltip, not draggable and not clickable.
     */
    @property({attribute: false})
    public calendarEntries: Issue[] = [];

    @state()
    private _rows: CalendarDayRow[] = [];

    @state()
    private _isDropTarget = false;

    @state()
    private _draggingIssueUid: string | null = null;

    /**
     * A day strictly before today can't be dropped onto at all (Anke,
     * 2026-09-09, follow-up to the "3 Tickets verschwanden" bug report: it
     * turned out to be user error, not the dissolve feature — she'd dropped
     * them onto a day that had already passed, so `_reclaimPastDates()` on
     * the parent view's very next `_load()` correctly cleared their
     * `personalDate` and moved them back to the Inbox, which just LOOKED
     * like data loss. Her actual ask: "bitte disable die Tage, die in der
     * Vergangenheit liegen... so dass ich in diese Tage gar nichts ablegen
     * kann"). Same `< today` comparison `_reclaimPastDates()` itself uses,
     * via ISO date strings. Always `false` for a cell in a future week —
     * those are never in the past by construction.
     */
    private get _isPast(): boolean {
        return !!this.date && this.date.toISODate()! < DateHelper.today.toISODate()!;
    }

    /** "Just moved here" flash (instant appear, 2s fade-out) — see
     *  `JustMovedFlashController`'s own doc comment for the full reasoning;
     *  shared with `<my-organizer-label-column>`/`<my-organizer-assigned-
     *  column>` instead of a 3rd private copy (review-fund, 2026-09-07). */
    private readonly _justMoved = new JustMovedFlashController(this);

    /** "Went DONE" dissolve animation (Anke, 2026-09-09) — see
     *  `DissolveFlashController`'s own doc comment and `liveResync()` below. */
    private readonly _dissolve = new DissolveFlashController(this);

    /** RxJS lifecycle (auto-unsubscribe on disconnect) for the window-event
     *  listener below — Composition, not manual `disconnectedCallback`
     *  bookkeeping, consistent with `<my-organizer-label-column>`/
     *  `<my-organizer-assigned-column>` (review-fund, 2026-09-07, round 2:
     *  this component used a raw `addEventListener`/`removeEventListener`
     *  pair here before, the one inconsistency left in an otherwise
     *  uniform-across-the-feature pattern). */
    private readonly _rxjs = new RxjsLifecycleController(this);

    private _seeded = false;

    /**
     * Seeds `_rows` from the `.rows` property exactly once — see the class
     * doc comment on why this cell doesn't keep re-syncing from it. A DONE
     * Issue is excluded outright here, never even shown for a moment — only
     * a LIVE transition into DONE (`liveResync()` below) gets the dissolve
     * animation (Anke, 2026-09-09: "es gehört ja dann im Zustand done auch
     * nicht mehr in die Ansicht rein").
     */
    public override willUpdate(changedProperties: PropertyValues): void {
        if (!this._seeded && changedProperties.has('rows')) {
            this._rows = this.rows.filter(pair => !IssueHelper.isDone(pair.issue));
            this._seeded = true;
        }
    }

    /**
     * Force-resyncs `_rows` from a freshly-reloaded, RAW (i.e. NOT already
     * DONE-filtered) bucket (Anke, 2026-09-09: "ich möchte, dass das Prinzip
     * [Live-Refresh] weiter genutzt wird" — follow-up to the Inbox/Boxen
     * live-refresh, then further follow-up: "wenn ein Ticket... auf done
     * gesetzt wird... möchte ich, dass man das Ticket noch kurz sieht und es
     * dann mit einer Auflöseanimation... verschwindet"). Called by the
     * parent view's own `EntityLiveRefreshController`, NOT via the `.rows`
     * property, which only ever seeds ONCE (see the class doc comment above
     * and `willUpdate`) — that guard exists to protect this cell's own
     * local drag&drop state from an unrelated parent re-render handing back
     * the SAME original bucket. A live entity-changed reload is the
     * opposite case: the parent has just re-fetched from the server and
     * this cell's local state is exactly what needs overwriting.
     *
     * `diffDoneRows()` (`domain/helpers/diff-done-rows.helper.ts`, shared
     * with `<my-organizer-label-column>`'s identical `_reloadLive()`) tells
     * us which currently-shown rows just went DONE — those are kept in
     * `_rows` for now and handed to `_dissolve.start()`, which animates
     * each away and only THEN actually removes it. Every other change
     * (title, status to something other than DONE, a brand new row, a row
     * simply no longer in the bucket) still updates `_rows` immediately.
     */
    public liveResync(rawRows: CalendarDayRow[]): void {
        // `uid => this._dissolve.has(uid)` (review-fund, 2026-09-09, round
        // 4): without it, a row already mid-dissolve from a PRIOR call
        // would be reported as `newlyDone` again on every subsequent one —
        // `DissolveFlashController.start()` restarts its ENTIRE timeline
        // rather than leaving an in-flight one alone, visibly snapping the
        // row back and re-pausing on every overlapping live-refresh.
        const {rows, newlyDone} = diffDoneRows(this._rows, rawRows, uid => this._dissolve.has(uid));
        this._rows = rows;

        for (const pair of newlyDone) {
            this._dissolve.start(pair.issue.uid, () => {
                this._rows = this._rows.filter(r => r.row.uid !== pair.row.uid);
            });
        }
    }

    public override connectedCallback(): void {
        super.connectedCallback();

        this._rxjs.listenUntilDisconnected<CustomEvent<MyOrganizerIssueDatedEventDetail>>(window, MY_ORGANIZER_ISSUE_DATED_EVENT)
            .subscribe(event => this._onIssueDated(event));
    }

    /**
     * The cross-cell live-sync listener (mirrors `<my-organizer-label-
     * column>`'s own `MY_ORGANIZER_ISSUE_LABELED_EVENT` listener): an Issue
     * re-dragged to a DIFFERENT day needs to disappear from every OTHER
     * cell's `_rows`, not just gain a row in its new one. `personalDate`
     * (not just `issueUid`) tells THIS cell "is this event about ME" — the
     * dispatching cell already applied the change to its own `_rows`
     * locally, reacting to its own event again here would just be a no-op
     * filter (the Issue is already gone from every OTHER cell's list).
     */
    private _onIssueDated(event: CustomEvent<MyOrganizerIssueDatedEventDetail>): void {
        const {issueUid, personalDate} = event.detail;
        if (this.date?.toISODate() === personalDate) {
            return;
        }
        this._rows = this._rows.filter(r => r.issue.uid !== issueUid);
    }

    private _onDragStart(event: DragEvent, issue: Issue): void {
        this._draggingIssueUid = issue.uid;
        event.dataTransfer?.setData('text/plain', issue.uid);
        if (event.dataTransfer) {
            event.dataTransfer.effectAllowed = 'move';
        }
        MyOrganizerDragContext.draggedIssue = issue;
    }

    private _onDragEnd(): void {
        this._draggingIssueUid = null;
    }

    /**
     * A past day never becomes a drop target (Anke, 2026-09-09) — NOT
     * calling `preventDefault()` here is what actually disables the drop
     * itself (standard HTML5 drag&drop: the browser only allows a drop on
     * an element whose `dragover` called `preventDefault()`), showing the
     * browser's own "not-allowed" cursor instead of the usual move cursor.
     */
    private _onDragOver(event: DragEvent): void {
        if (this._isPast) {
            return;
        }
        event.preventDefault();
        this._isDropTarget = true;
    }

    private _onDragLeave(): void {
        this._isDropTarget = false;
    }

    private _onEditIssue(issue: Issue): void {
        void IssueHelper.openIssueEditDialog(issue);
    }

    /**
     * The single entry point for a drop — sets the dropped Issue's
     * `personalDate` to THIS cell's own `date` and dispatches Create (no row
     * exists yet at all) or Update (a row exists — already labeled, already
     * dated elsewhere, or both), mirroring `<my-organizer-label-column>`'s
     * own Assign-vs-Move split, just for one field instead of two commands.
     * `personalLabel` is deliberately left untouched either way — dropping
     * onto a calendar day is purely additive (Anke's request only describes
     * the date being stored, nothing about clearing a label).
     */
    private async _onDrop(event: DragEvent): Promise<void> {
        event.preventDefault();
        this._isDropTarget = false;

        // Belt-and-suspenders — `_onDragOver()`'s missing `preventDefault()`
        // already stops the browser from firing `drop` here at all in the
        // normal case, but a defensive guard costs nothing (Anke, 2026-09-09).
        if (this._isPast) {
            return;
        }

        if (!this.date) {
            console.error('[my-organizer-calendar-day] dropped without a .date bound — nothing to persist');
            return;
        }

        const issue = MyOrganizerDragContext.draggedIssue;
        MyOrganizerDragContext.draggedIssue = null;
        if (!issue) {
            return;
        }

        // Already shown in THIS cell — nothing to persist.
        if (this._rows.some(r => r.issue.uid === issue.uid)) {
            return;
        }

        const isoDate = this.date.toISODate()!;
        const repository = container.resolve(IssuePersonalDataRepository);
        const existingRow = await repository.findOneByIssue(issue.uid);
        const commandBus = container.resolve(CommandBus);

        if (existingRow) {
            existingRow.personalDate = this.date;
            const dispatched = await commandBus.dispatchCommand(
                new UpdateIssuePersonalDataCommand('issue-personal-data', existingRow.uid, existingRow)
            );
            if (!dispatched) {
                console.error('[my-organizer-calendar-day] failed to set the Issue\'s personal date');
                return;
            }
            this._addRow({issue, row: existingRow}, issue.uid, isoDate);
            return;
        }

        const row = await repository.create(issue);
        row.personalDate = this.date;
        const dispatched = await commandBus.dispatchCommand(
            new CreateIssuePersonalDataCommand('issue-personal-data', row.uid, row)
        );
        if (!dispatched) {
            console.error('[my-organizer-calendar-day] failed to create the Issue\'s personal data row');
            return;
        }
        this._addRow({issue, row}, issue.uid, isoDate);
    }

    private _addRow(pair: CalendarDayRow, issueUid: string, isoDate: string): void {
        this._rows = [...this._rows, pair];
        this._justMoved.flash(issueUid);
        window.dispatchEvent(new CustomEvent<MyOrganizerIssueDatedEventDetail>(
            MY_ORGANIZER_ISSUE_DATED_EVENT,
            {detail: {issueUid, personalDate: isoDate}}
        ));
    }

    public render(): HTMLTemplateResult {
        if (!this.date) {
            return html``; // no `.date` bound — see the property's own doc comment.
        }

        return html`
            <div class="my-organizer-calendar-day ${this._isDropTarget ? 'my-organizer-calendar-day--drop-target' : ''} ${this._isPast ? 'my-organizer-calendar-day--past' : ''}"
                 @dragover=${(event: DragEvent) => this._onDragOver(event)}
                 @dragleave=${() => this._onDragLeave()}
                 @drop=${(event: DragEvent) => this._onDrop(event)}>
                <div class="my-organizer-calendar-day__header">
                    <span class="my-organizer-calendar-day__weekday">${this.label}</span>
                    <span class="my-organizer-calendar-day__date">${this.date.toLocaleString(DATE_FORMAT)}</span>
                    ${this._renderCalendarEntries()}
                </div>
                <div class="my-organizer-calendar-day__body">
                    ${repeat(this._rows, pair => pair.row.uid, pair => this._renderRow(pair))}
                </div>
            </div>
        `;
    }

    private _renderCalendarEntries(): HTMLTemplateResult | typeof nothing {
        if (this.calendarEntries.length === 0) {
            return nothing;
        }

        return html`
            <div class="my-organizer-calendar-day__calendar-entries">
                ${repeat(this.calendarEntries, entry => entry.uid, entry => html`
                    <div class="my-organizer-calendar-day__calendar-entry" title=${entry.title}>${entry.title}</div>
                `)}
            </div>
        `;
    }

    private _renderRow(pair: CalendarDayRow): HTMLTemplateResult {
        const {issue} = pair;

        return html`
            <div class=${classMap({
                'my-organizer-assigned-column__row': true,
                'my-organizer-calendar-day__row': true,
                'my-organizer-assigned-column__row--dragging': this._draggingIssueUid === issue.uid,
                'my-organizer-assigned-column__row--just-moved': this._justMoved.isJustMoved(issue.uid),
                'my-organizer-assigned-column__row--just-moved-fading': this._justMoved.isFading(issue.uid),
                'my-organizer-assigned-column__row--dissolving': this._dissolve.isDissolving(issue.uid),
            })}
                 draggable="true"
                 @dragstart=${(event: DragEvent) => this._onDragStart(event, issue)}
                 @dragend=${() => this._onDragEnd()}
                 @click=${() => this._onEditIssue(issue)}
                 data-root-type=${rootUidOf(issue.type) ?? nothing}>
                ${issue.status ? html`<div class="my-organizer-assigned-column__status"
                     style=${styleMap(itemColorStyle(issue.status.color, issue.status.textColor))}>${issue.status.key}</div>` : nothing}
                <div class="my-organizer-assigned-column__date">${issue.start.toLocaleString(DATE_FORMAT)}</div>
                <div class="my-organizer-assigned-column__title">${issue.title != '' ? issue.title : '-'}</div>
            </div>
        `;
    }

}
